import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  message,
} from 'antd';
import { CalendarOutlined, ToolOutlined, UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { operatorStepAction, operatorRecoverStep } from '../modules/routes/api';
import { rescheduleOrder } from '../modules/orders/api';

/**
 * OVO-020 — Operator action panel.
 *
 * Surfaces, on the planner order-detail page, the same set of state
 * transitions a driver has in the mobile app — minus photo upload and
 * QR scanning. Used to unstick orders when the driver app failed
 * (network loss, validation bug, lost device, abandoned route, …).
 *
 * Every action submitted here hits the backend wrapper that calls into
 * the mobile state machine with `simulatedByOperator=true`, so the
 * resulting OrderLog/RouteLog rows are stamped accordingly — keeping a
 * clear audit trail of operator vs driver actions.
 *
 * @param {Object} props
 * @param {Object} props.order - full order object (must include status,
 *   routeId, and steps[] with { id, type })
 * @param {Function} props.onSuccess - called after a successful action
 *   so the parent can refresh the order.
 */

// Status → array of valid next transitions, mirroring the driver-app
// state-machine. Terminal statuses (COMPLETED, *_FAILED, SKIPPED, ERROR)
// have no entries → panel renders a "no actions available" notice.
const NEXT_TRANSITIONS = {
  ASSIGNED: ['PICKUP_ARRIVED', 'SKIPPED'],
  OPTIMIZED: ['PICKUP_ARRIVED', 'SKIPPED'],
  DISPATCHED: ['PICKUP_ARRIVED', 'SKIPPED'],
  PICKUP_ARRIVED: ['COLLECTED', 'PICKUP_FAILED', 'SKIPPED'],
  COLLECTED: ['DELIVERY_ARRIVED', 'SKIPPED'],
  IN_TRANSIT: ['DELIVERY_ARRIVED', 'SKIPPED'],
  DELIVERY_ARRIVED: ['COMPLETED', 'DELIVERY_FAILED'],
};

// Human labels + which step type (pickup / delivery) the transition acts
// on. The frontend slices the order's `steps[]` to that type before
// sending — the backend uses these stepIds to apply the transition.
const TRANSITION_META = {
  PICKUP_ARRIVED: { label: 'Mark Pickup Arrived', stepType: 'pickup', buttonType: 'default' },
  COLLECTED: { label: 'Confirm Pickup', stepType: 'pickup', buttonType: 'primary' },
  PICKUP_FAILED: { label: 'Fail Pickup', stepType: 'pickup', buttonType: 'default', danger: true },
  DELIVERY_ARRIVED: { label: 'Mark Delivery Arrived', stepType: 'delivery', buttonType: 'default' },
  COMPLETED: { label: 'Mark Completed', stepType: 'delivery', buttonType: 'primary' },
  DELIVERY_FAILED: { label: 'Fail Delivery', stepType: 'delivery', buttonType: 'default', danger: true },
  SKIPPED: { label: 'Skip', stepType: 'delivery', buttonType: 'default', danger: true },
};

// OVO-224 — statuses an operator can RECOVER (un-skip / undo a wrong failure):
// reverted to the pre-failure deliverable state so the normal actions resume.
const RECOVERABLE_STATUSES = ['SKIPPED', 'DELIVERY_FAILED', 'PICKUP_FAILED'];

// request.js hands back the response body itself as `error` ({ message, statusCode }),
// so `error.data.message` never existed and every refusal read "Action failed" —
// e.g. Sofia's "Pickup enrichment required — …" (Oct 26). Nest validation errors
// carry an array.
const errorText = (error, fallback) => {
  const m = error?.message ?? error?.data?.message;
  if (Array.isArray(m)) return m.join(' · ') || fallback;
  return m || fallback;
};

// Issue types — MUST match the backend `ISSUE_TYPE` constant
// (src/orders/constants): the mobile ActionStepDto validates against it, so
// any value outside this list 400s. (The previous list here — NOT_HOME /
// REFUSED / DAMAGED / NO_ACCESS — was invented and made those picks fail.)
const ISSUE_TYPES = [
  'CLIENT_UNAVAILABLE',
  'WRONG_ADDRESS',
  'WRONG_CODE',
  'CUSTOMER_REQUEST',
  'DELIVERY_INFORMATION',
  'RETURN_ISSUE',
  'DELAY',
  'PRODUCT_DAMAGE',
  'PLANNING_ROUTE_ISSUE',
  'LOCATION_CLOSED_ACCESS_BLOCKED',
  'NOTHING_TO_COLLECT',
  'TECHNICAL_ISSUE',
  'OTHER',
];

const PACKAGE_WAS_OPTIONS = [
  { value: 'RECEIVED_BY_CUSTOMER', label: 'Received by customer' },
  { value: 'LEFT_AT_DOOR', label: 'Left at door' },
  { value: 'LEFT_WITH_NEIGHBOR', label: 'Left with neighbor' },
  { value: 'PICKED_UP_LATER', label: 'Picked up later' },
];

export default function OperatorActionPanel({ order, onSuccess }) {
  const [openStatus, setOpenStatus] = useState(null); // which transition modal is open
  const [submitting, setSubmitting] = useState(false);
  const [recovering, setRecovering] = useState(false); // OVO-224 recover-from-skip
  // OVO-252 — reschedule a skipped/failed order to a new date.
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(() => dayjs().add(1, 'day'));
  const [rescheduling, setRescheduling] = useState(false);
  const [form] = Form.useForm();

  const transitions = NEXT_TRANSITIONS[order?.status] || [];

  // Resolve step IDs for the target status, filtered to the right type.
  const stepIdsForStatus = useMemo(() => {
    if (!openStatus) return [];
    const meta = TRANSITION_META[openStatus];
    if (!meta) return [];
    return (order?.steps || []).filter((s) => String(s.type).toLowerCase() === meta.stepType).map((s) => s.id);
  }, [openStatus, order?.steps]);

  // OVO-020 Phase 1.5 — pickup-enrichment capture rows that the driver
  // never filled in. For each OrderProduct where requiresWeight=true and
  // the dispatcher left weight blank AND no matching CollectedItem
  // exists yet, the operator must enter a weight before COLLECTED can
  // proceed (otherwise the backend's pickup-enrichment guard rejects).
  const pendingWeightCaptures = useMemo(() => {
    const pickupStep = (order?.steps || []).find((s) => String(s.type).toLowerCase() === 'pickup');
    if (!pickupStep) return [];
    const captured = order?.collectedItems || [];
    return (order?.orderProducts || [])
      .filter((op) => {
        const t = op.partnerItemType;
        if (!t?.requiresWeight) return false;
        if (op.weight != null) return false;
        const hasCapture = captured.some((c) => c.partnerItemTypeId === op.partnerItemTypeId && c.weight != null);
        return !hasCapture;
      })
      .map((op) => ({
        orderId: order.id,
        partnerItemTypeId: op.partnerItemTypeId,
        partnerItemTypeName: op.partnerItemType?.name || `Type #${op.partnerItemTypeId}`,
        unit: op.partnerItemType?.unitOfMeasure || 'kg',
        routeStepId: pickupStep.id,
      }));
  }, [order?.orderProducts, order?.collectedItems, order?.steps, order?.id]);

  if (!order || !order.routeId) {
    return null; // operator panel only meaningful for orders on a route
  }

  const handleOpen = (status) => {
    form.resetFields();
    setOpenStatus(status);
  };

  const handleCancel = () => {
    setOpenStatus(null);
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (stepIdsForStatus.length === 0) {
      message.error('No matching route steps for this action.');
      return;
    }
    // OVO-020 Phase 1.5 — gather pickup-enrichment captures from the form
    // when confirming a pickup. Backend creates the CollectedItem rows
    // before delegating to actionSteps so the guard passes.
    const enrichmentCaptures =
      openStatus === 'COLLECTED'
        ? pendingWeightCaptures
            .map((c) => {
              const w = values[`weight_${c.partnerItemTypeId}`];
              if (w == null || Number.isNaN(Number(w))) return null;
              return {
                orderId: c.orderId,
                partnerItemTypeId: c.partnerItemTypeId,
                routeStepId: c.routeStepId,
                weight: Number(w),
              };
            })
            .filter(Boolean)
        : [];

    setSubmitting(true);
    const payload = {
      status: openStatus,
      stepIds: stepIdsForStatus,
      uncheckedStepIds: [],
      files: [],
      customerNote: values.customerNote || '',
      packageWas: values.packageWas || '',
      issueType: values.issueType || undefined,
      ...(enrichmentCaptures.length > 0 ? { enrichmentCaptures } : {}),
    };
    const { response, error } = await operatorStepAction(order.routeId, payload);
    setSubmitting(false);
    if (error) {
      message.error(errorText(error, 'Action failed'));
      return;
    }
    message.success('Action recorded. Audit log updated.');
    setOpenStatus(null);
    onSuccess?.(response);
  };

  // OVO-224 — recover a wrongly-skipped/failed order. The backend reverts it to
  // the pre-failure deliverable state (delivery → Collected, pickup → Assigned)
  // and reopens the step, so the transition buttons above come back.
  const handleRecover = async () => {
    const stepIds = (order?.steps || []).map((s) => s.id);
    if (stepIds.length === 0) {
      message.error('No route steps to recover.');
      return;
    }
    setRecovering(true);
    const { response, error } = await operatorRecoverStep(order.routeId, { stepIds });
    setRecovering(false);
    if (error) {
      message.error(errorText(error, 'Recovery failed'));
      return;
    }
    message.success('Order recovered — you can now complete it with the actions above.');
    onSuccess?.(response);
  };

  // OVO-252 — the OTHER path out of a skip/failure: the attempt was real, so
  // plan the delivery again. Detaches the order from the old route, sets the
  // new date, resets to UNASSIGNED (schedulable) and bumps deliveryAttempts.
  const handleReschedule = async () => {
    if (!rescheduleDate) {
      message.error('Pick the new delivery date.');
      return;
    }
    setRescheduling(true);
    const { response, error } = await rescheduleOrder(order.orderCode, {
      deliveryDate: rescheduleDate.format('YYYY-MM-DD'),
    });
    setRescheduling(false);
    if (error) {
      message.error(errorText(error, 'Reschedule failed'));
      return;
    }
    setRescheduleOpen(false);
    message.success(
      `Order rescheduled for ${rescheduleDate.format('YYYY-MM-DD')} — it is back in the unscheduled pool (attempt ${
        (response?.deliveryAttempts || 0) + 1
      }).`,
    );
    onSuccess?.(response);
  };

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div
      className="operator-action-panel"
      style={{
        background: '#fffbe6',
        border: '1px solid #ffe58f',
        padding: 12,
        borderRadius: 4,
        marginTop: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <ToolOutlined style={{ color: '#d48806' }} />
        <strong>Operator actions</strong>
        <Tag color="orange" style={{ fontSize: 11 }}>
          simulates driver
        </Tag>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
        Use this only when the driver app is stuck. Every action is logged with your name and flagged as
        operator-simulated in the audit trail. Photo upload and QR scan are not supported here.
      </p>
      {transitions.length === 0 ? (
        RECOVERABLE_STATUSES.includes(order.status) ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message={`Order is "${order.status}".`}
              description="Driver mistake? Recover it back to a deliverable state and finish it here. Real failed attempt? Reschedule it to a new date — it returns to the unscheduled pool and the attempt is counted."
            />
            <Space wrap>
              <Popconfirm
                title="Recover this order?"
                description="Reverts the skip/failure to a deliverable state so you can complete it. Logged as operator-simulated."
                okText="Recover"
                cancelText="Cancel"
                okButtonProps={{ loading: recovering }}
                onConfirm={handleRecover}
              >
                <Button type="primary" icon={<UndoOutlined />}>
                  Recover (undo skip/fail)
                </Button>
              </Popconfirm>
              {/* OVO-252 */}
              <Button icon={<CalendarOutlined />} onClick={() => setRescheduleOpen(true)}>
                Reschedule
              </Button>
            </Space>
            <Modal
              title={`Reschedule ${order.orderCode}`}
              open={rescheduleOpen}
              onOk={handleReschedule}
              okText="Reschedule"
              confirmLoading={rescheduling}
              onCancel={() => setRescheduleOpen(false)}
            >
              <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                The order leaves its current route and goes back to the unscheduled pool as UNASSIGNED on the chosen
                date — plan it from the Route Plan as usual. The failed attempt stays in the order timeline and the
                attempts counter is incremented.
              </p>
              <Form layout="vertical">
                <Form.Item label="New delivery date" required>
                  <DatePicker
                    style={{ width: '100%' }}
                    value={rescheduleDate}
                    onChange={setRescheduleDate}
                    disabledDate={(d) => d && d.isBefore(dayjs().startOf('day'))}
                  />
                </Form.Item>
              </Form>
            </Modal>
          </Space>
        ) : (
          <Alert type="info" showIcon message={`No operator actions available for status "${order.status}".`} />
        )
      ) : (
        <Space wrap>
          {transitions.map((status) => {
            const meta = TRANSITION_META[status];
            return (
              <Button key={status} type={meta.buttonType} danger={!!meta.danger} onClick={() => handleOpen(status)}>
                {meta.label}
              </Button>
            );
          })}
        </Space>
      )}

      <Modal
        title={openStatus ? TRANSITION_META[openStatus]?.label : ''}
        open={!!openStatus}
        onCancel={handleCancel}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Confirm"
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`This will mark order ${order.orderCode} as ${openStatus}. The action is audit-logged as operator-simulated.`}
        />
        <Form layout="vertical" form={form}>
          {/* OVO-020 Phase 1.5 — driver-side pickup-weight captures that
              are still missing. Required, since the backend guard would
              otherwise reject the COLLECTED transition. Accepts 0
              (matches OVO-017) and comma decimals (matches the driver
              app's input). */}
          {openStatus === 'COLLECTED' && pendingWeightCaptures.length > 0 && (
            <>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Driver weight capture is missing"
                description={
                  <span>
                    Enter the weight(s) the driver was supposed to record on site. These will be stored as collected
                    items on the order, attributed to you as the operator.
                  </span>
                }
              />
              {pendingWeightCaptures.map((c) => (
                <Form.Item
                  key={c.partnerItemTypeId}
                  name={`weight_${c.partnerItemTypeId}`}
                  label={`Weight — ${c.partnerItemTypeName} (${c.unit})`}
                  rules={[{ required: true, message: 'Enter a weight (use 0 if nothing was picked up)' }]}
                >
                  <InputNumber
                    min={0}
                    decimalSeparator="."
                    style={{ width: 200 }}
                    parser={(v) =>
                      v == null
                        ? ''
                        : String(v)
                            .replace(/,/g, '.')
                            .replace(/[^\d.\-]/g, '')
                    }
                    formatter={(v) => (v == null || v === '' ? '' : String(v))}
                  />
                </Form.Item>
              ))}
            </>
          )}
          {openStatus === 'COMPLETED' && (
            <Form.Item
              name="packageWas"
              label="Package was"
              rules={[{ required: true, message: 'Pick how the package was delivered' }]}
            >
              <Select options={PACKAGE_WAS_OPTIONS} placeholder="Select" />
            </Form.Item>
          )}
          {['DELIVERY_FAILED', 'PICKUP_FAILED', 'SKIPPED'].includes(openStatus) && (
            <Form.Item name="issueType" label="Reason" rules={[{ required: true, message: 'Pick a reason' }]}>
              <Select options={ISSUE_TYPES.map((v) => ({ value: v, label: v }))} placeholder="Select reason" />
            </Form.Item>
          )}
          <Form.Item name="customerNote" label="Note (optional)">
            <Input.TextArea rows={3} placeholder="Add any relevant context (will land in the order's audit trail)." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
